import { Head, router } from "@inertiajs/react"
import {
  Check,
  ChevronDown,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import {
  ColumnManager,
  ColumnManagerTrigger,
  mergeReportColumnCatalog,
  sortRulesWithoutGrouping,
} from "@/components/products/reports/ColumnManager"
import DownloadReportDialog, {
  type DownloadFormat,
} from "@/components/products/reports/DownloadReportDialog"
import { SaveViewModal } from "@/components/products/reports/SaveViewModal"
import SendReportDialog from "@/components/products/reports/SendReportDialog"
import SubscriptionsDialog from "@/components/products/reports/SubscriptionsDialog"
import {
  type SavedView,
  ViewPicker,
  reportViewIssueMessage,
} from "@/components/products/reports/ViewPicker"
import SendHistorySection, {
  type SendHistoryEntry,
} from "@/components/shared/send-history-section"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import {
  type WeightUnit,
  formatWeightValue,
  toDisplayUnits,
  weightDecimals,
} from "@/hooks/use-weight-unit"
import AppLayout from "@/layouts/app-layout"
import { csrfFetch } from "@/lib/csrf-fetch"
import {
  REPORT_DATE_PRESETS,
  reportConfigFingerprint,
  reportDatePreset,
  reportDatePresetForTrailingDays,
  resolveReportDatePreset,
} from "@/lib/report-scope"
import { cn } from "@/lib/utils"
import type {
  BreadcrumbItem,
  ReportColumn,
  ReportFilterOption,
  ReportFilterSpec,
  ReportPageCapabilities,
  ReportRow,
  ReportScopeFilterValue,
  ReportSortRule,
  ReportSubscriptionData,
  ReportViewConfig,
  SavedReportRecipientData,
} from "@/types"

// Row hashes from Ruby may carry one of these sentinels:
//   __group_header  — render as a single bold cell spanning every column
//   __group_subtotal — sum row at end of a group (per Locked Visual Spec)
//   __group_subtotal_label — group label owned only by the subtotal row
//   __grand_total    — final aggregate row at the very end
// All honored by both the rendered table and the CSV/XLSX/PDF exports
// (the rendering pipeline for each shares this sentinel set).
const GROUP_HEADER_KEY = "__group_header"
const GROUP_HEADER_DEPTH_KEY = "__group_depth"
const GROUP_SUBTOTAL_KEY = "__group_subtotal"
const GROUP_SUBTOTAL_LABEL_KEY = "__group_subtotal_label"
const GRAND_TOTAL_KEY = "__grand_total"
const SORT_ORIGIN_KEY = "__report_sort_origin"

// Filter values are loosely typed: strings for date/string/select,
// boolean for boolean, string[] for multi_select, {start,end} for
// date_range. The server-side permit list does the type discipline.
interface DateRangeValue {
  start: string
  end: string
  preset?: string
}
type FilterValue = string | boolean | string[] | DateRangeValue | undefined
type FilterValues = Record<string, FilterValue>

function isDateRange(v: FilterValue): v is DateRangeValue {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    "start" in v &&
    "end" in v
  )
}

// Returns the trailing-N-days range ending today, as ISO YYYY-MM-DD
// strings in the user's local timezone. Used to pre-fill the from/to
// inputs when the URL has no date params, so the visible default
// matches what the server applies.
function daysAgoRange(days: number): DateRangeValue {
  const preset = reportDatePresetForTrailingDays(days)
  if (preset) {
    const resolved = resolveReportDatePreset(preset.key)
    if (resolved) return resolved
  }
  const today = new Date()
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`
  const start = new Date(today)
  start.setDate(start.getDate() - (days - 1))
  return { start: fmt(start), end: fmt(today) }
}

interface OrgUser {
  id: number
  name: string
  email: string
}

type ActiveSavedView = SavedView & { config: ReportViewConfig }

export type ReportViewConflictKind = "stale" | "deleted" | null

export function reportViewConflictKind(
  status: number,
  errorCode: string | undefined,
  hasCurrentView: boolean,
): ReportViewConflictKind {
  if (status !== 409 || errorCode !== "stale_view") return null
  return hasCurrentView ? "stale" : "deleted"
}

interface Props {
  report: {
    key: string
    title: string
    description: string
    /** All declared columns (full set — the future ColumnManager needs this). */
    columns: ReportColumn[]
    /**
     * Pre-filtered column list the table renders. Computed by the controller
     * by removing any column the report is grouped by (single source of
     * truth — PDF + Inertia receive the same array). Falls back to the
     * full columns array when no grouping is active.
     */
    visible_columns: ReportColumn[]
    /**
     * Keys the active view legitimately selected that this org cannot render —
     * today only a "(lbs)" companion on a pounds org. They are NOT shown, but
     * they are unioned back in when saving so an edit made while the column is
     * invisible cannot silently delete it, and into the dirty baseline so their
     * absence is not read as an unsaved change. Flip the org's unit back and the
     * column reappears on its own.
     */
    suppressed_columns?: string[]
    rows: ReportRow[]
    filter_spec: ReportFilterSpec[]
    filter_values: FilterValues
    sort: ReportSortRule[]
    subscribable: boolean
    /** Whether this report can be downloaded as a ZIP of one PDF per customer. */
    can_split_by_customer: boolean
    /** Whether this report can be emailed as one privacy-scoped copy per customer. */
    per_customer_sendable: boolean
    my_subscription_for_active_view: ReportSubscriptionData | null
    active_subscriber_count: number | null
    saved_recipients: SavedReportRecipientData[]
  }
  active_view: ActiveSavedView | null
  available_views: SavedView[]
  capabilities: ReportPageCapabilities
  // Still emitted by ReportsController; nothing reads it since the
  // per-customer send fan-out (and its customer master data) left with the
  // transload product. Kept in the contract so the prop-spec stays honest.
  can_edit_report_contacts: boolean
  send_history: SendHistoryEntry[]
  org_users: OrgUser[]
}

function todayStr(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

// Columns that hold numbers should hang right + use tabular figures so
// digits line up column-to-column. Dates stay left-aligned (they read as
// text). String values stay left as well.
function isNumericFormat(format: ReportColumn["format"]) {
  return (
    format === "weight" ||
    format === "count" ||
    format === "integer" ||
    format === "currency"
  )
}

/**
 * Re-add the column keys the server could not render for this organization.
 *
 * Used at exactly two facilities, and both are required. The SAVE payload needs them
 * or an unrelated edit made while the column is invisible silently deletes a
 * selection the operator cannot see. The DIRTY BASELINE needs them or that same
 * absence reads as an unsaved change and the "Modified from" badge never clears.
 *
 * The two are currently coupled: `currentViewConfig` is both the save payload and
 * the current side of the dirty compare, so dropping either call surfaces as a
 * view that opens already claiming "Modified from" (verified by removing each in
 * turn). Keep them as two calls anyway — decoupling the save from the compare is
 * a plausible future refactor, and the spec asserts the persisted config
 * separately so that refactor cannot quietly start dropping the key.
 */
/**
 * The catalog minus opt-in columns — i.e. what the server renders when nobody has
 * chosen. Any baseline compared against the rendered set must use this, or an org
 * whose catalog contains an opt-in column looks edited before it is touched.
 */
export function renderableColumnKeys(columns: ReportColumn[]): string[] {
  return columns.filter((c) => !c.default_hidden).map((c) => c.key)
}

export function withSuppressedColumns(
  keys: string[],
  suppressed: string[],
): string[] {
  if (suppressed.length === 0) return keys
  const seen = new Set(keys)
  return [...keys, ...suppressed.filter((key) => !seen.has(key))]
}

/**
 * `unit` is the COLUMN's declared unit, not the org's. Reading the org unit here
 * (via useWeightUnit) would render the opt-in pounds companion in tons and put
 * the page at odds with the server-rendered CSV/XLSX/PDF, which convert per
 * column. Row values are always canonical pounds; conversion is display-only.
 */
export function formatCell(
  value: ReportRow[string],
  format: ReportColumn["format"],
  signed = false,
  unit?: string,
) {
  if (value === null || value === undefined || value === "") return "—"
  const sign =
    signed && typeof value === "number" && value !== 0
      ? value > 0
        ? "+"
        : "−"
      : ""
  const displayValue =
    signed && typeof value === "number" ? Math.abs(value) : value
  switch (format) {
    case "weight": {
      if (typeof displayValue !== "number") return String(displayValue)
      const weightUnit: WeightUnit =
        unit === "tons" || unit === "tonnes" ? unit : "lbs"
      // The sign comes from the value AS RENDERED, not from the raw pounds. A
      // 5 lb movement is 0.0025 tons, which prints "0.00" — there is no direction
      // left at that precision, and the server's exports print it unsigned, so
      // signing it here would show "−0.00" on screen beside "0.00" in the CSV.
      const rounded = Number(
        toDisplayUnits(displayValue, weightUnit).toFixed(
          weightDecimals(weightUnit),
        ),
      )
      const weightSign = rounded === 0 ? "" : sign
      // formatWeightValue holds the divisor + precision rule, shared with
      // useWeightUnit().formatWeight so the two client surfaces cannot drift.
      return `${weightSign}${formatWeightValue(displayValue, weightUnit)}`
    }
    case "integer":
      return typeof displayValue === "number"
        ? `${sign}${displayValue.toLocaleString()}`
        : String(displayValue)
    case "count":
      return typeof displayValue === "number"
        ? `${sign}${displayValue.toLocaleString()} ${displayValue === 1 ? "package" : "packages"}`
        : String(displayValue)
    case "currency":
      // Server stores cents; format as dollars with 2 decimal places.
      return typeof displayValue === "number"
        ? `${sign}$${(displayValue / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : String(value)
    case "date": {
      // Parse YYYY-MM-DD into a local Date to avoid the UTC-shift pitfall.
      const s = String(value)
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      }
      try {
        return new Date(s).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      } catch {
        return s
      }
    }
    default:
      return String(value)
  }
}

function blankSortValue(value: ReportRow[string]): boolean {
  return value === null || value === undefined || value === ""
}

function normalizedSortValue(
  value: Exclude<ReportRow[string], null | undefined>,
): [number, number | string] {
  if (typeof value === "number") return [0, value]
  return [3, String(value).toLowerCase()]
}

function compareSortValues(
  left: ReportRow[string],
  right: ReportRow[string],
  direction: ReportSortRule["dir"],
): number {
  const leftBlank = blankSortValue(left)
  const rightBlank = blankSortValue(right)
  if (leftBlank && rightBlank) return 0
  if (leftBlank) return 1
  if (rightBlank) return -1

  const [leftType, leftValue] = normalizedSortValue(left as string | number)
  const [rightType, rightValue] = normalizedSortValue(right as string | number)
  let comparison = leftType - rightType
  if (comparison === 0) {
    comparison = leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0
  }
  return direction === "desc" ? comparison * -1 : comparison
}

function isReportSummaryRow(row: ReportRow): boolean {
  return Boolean(
    row[GROUP_HEADER_KEY] || row[GROUP_SUBTOTAL_KEY] || row[GRAND_TOTAL_KEY],
  )
}

/**
 * Client-side preview of durable row sorting. Summary rows remain fixed, so
 * each contiguous leaf group's data is sorted without moving its headers or
 * subtotal. The server-provided origin restores native report order when an
 * operator removes a previously saved sort.
 */
export function sortReportRows(
  rows: ReportRow[],
  rules: ReportSortRule[],
): ReportRow[] {
  const sortRun = (run: ReportRow[]) => {
    const hasOrigins = run.some(
      (row) => typeof row[SORT_ORIGIN_KEY] === "number",
    )
    if (rules.length === 0 && !hasOrigins) return run

    return run
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        for (const rule of rules) {
          const comparison = compareSortValues(
            left.row[rule.key],
            right.row[rule.key],
            rule.dir,
          )
          if (comparison !== 0) return comparison
        }
        const leftOrigin = left.row[SORT_ORIGIN_KEY]
        const rightOrigin = right.row[SORT_ORIGIN_KEY]
        return (
          (typeof leftOrigin === "number" ? leftOrigin : left.index) -
          (typeof rightOrigin === "number" ? rightOrigin : right.index)
        )
      })
      .map(({ row }) => row)
  }

  const result: ReportRow[] = []
  let pending: ReportRow[] = []
  const flush = () => {
    result.push(...sortRun(pending))
    pending = []
  }
  rows.forEach((row) => {
    if (isReportSummaryRow(row)) {
      flush()
      result.push(row)
    } else {
      pending.push(row)
    }
  })
  flush()
  return result
}

// Truthy-string boolean used by Rails params (e.g. "true" / "1").
function asBool(v: unknown): boolean {
  if (typeof v === "boolean") return v
  if (typeof v === "string") return ["true", "1", "yes", "on"].includes(v)
  return false
}

// Coerce a raw filter value (from props) into the shape its widget
// expects. Multi-selects: always an array. Booleans: actual bool.
// date_range: { start, end } object. Everything else: string or "".
function coerceInitial(
  spec: ReportFilterSpec,
  raw: FilterValue,
  filterValues: FilterValues,
  savedDatePreset?: string,
  explicitDateOverride = false,
  urlDatePreset?: string | null,
): FilterValue {
  if (spec.kind === "boolean") {
    return asBool(raw) || Boolean(spec.default)
  }
  if (spec.kind === "multi_select") {
    if (Array.isArray(raw)) return raw
    if (typeof raw === "string" && raw.length > 0) return [raw]
    return []
  }
  if (spec.kind === "date_range") {
    // date_range filters DON'T have a single value under `spec.key` —
    // they pull from the two URL params named in start_key / end_key.
    // When BOTH are missing AND the spec declares `default_days`, we
    // pre-fill the inputs with that window so the UI matches the
    // server's default behavior.
    const startKey = spec.start_key ?? "start_date"
    const endKey = spec.end_key ?? "end_date"
    const start = filterValues[startKey]
    const end = filterValues[endKey]
    const haveStart = typeof start === "string" && start.length > 0
    const haveEnd = typeof end === "string" && end.length > 0
    const explicitPreset = reportDatePreset(urlDatePreset)
    if (explicitPreset && (haveStart || haveEnd)) {
      return {
        start: haveStart ? (start as string) : "",
        end: haveEnd ? (end as string) : "",
        preset: explicitPreset.key,
      }
    }
    const resolvedPreset = reportDatePreset(savedDatePreset)
      ? resolveReportDatePreset(savedDatePreset as string)
      : null
    if (resolvedPreset && !explicitDateOverride && (haveStart || haveEnd)) {
      return {
        start: haveStart ? (start as string) : "",
        end: haveEnd ? (end as string) : "",
        preset: resolvedPreset.preset,
      }
    }
    if (!explicitDateOverride && haveStart && haveEnd) {
      const inferredPreset = REPORT_DATE_PRESETS.map((preset) =>
        resolveReportDatePreset(preset.key),
      ).find((resolved) => resolved?.start === start && resolved?.end === end)
      if (inferredPreset) return inferredPreset
    }
    if (!haveStart && !haveEnd && spec.default_days) {
      return daysAgoRange(spec.default_days)
    }
    return {
      start: haveStart ? (start as string) : "",
      end: haveEnd ? (end as string) : "",
    }
  }
  if (typeof raw === "string") return raw
  return typeof spec.default === "string" ? spec.default : ""
}

// Short "Apr 15" style label for a YYYY-MM-DD ISO date.
function shortDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return iso
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

// Returns a short label describing the filter's current value, or null
// when it's empty / at the default. Drives the "Status: 2 selected"
// suffix on each filter button.
function filterSummary(
  spec: ReportFilterSpec,
  value: FilterValue,
): string | null {
  switch (spec.kind) {
    case "date_range": {
      if (!isDateRange(value)) return null
      if (!value.start && !value.end) return null
      const preset = reportDatePreset(value.preset)
      if (preset) return preset.label
      if (spec.default_days) {
        const def = daysAgoRange(spec.default_days)
        if (value.start === def.start && value.end === def.end) {
          return `Last ${spec.default_days} days`
        }
      }
      if (value.start && value.end) {
        return `${shortDate(value.start)} – ${shortDate(value.end)}`
      }
      if (value.start) return `from ${shortDate(value.start)}`
      return `to ${shortDate(value.end)}`
    }
    case "multi_select": {
      const arr = (value as string[]) ?? []
      if (arr.length === 0) return null
      const opts = (spec.options as ReportFilterOption[] | undefined) ?? []
      if (arr.length === 1) {
        const opt = opts.find((o) => o.value === arr[0])
        return opt?.label ?? arr[0]
      }
      return `${arr.length} selected`
    }
    case "select": {
      if (typeof value !== "string" || !value) return null
      const opts = (spec.options as ReportFilterOption[] | undefined) ?? []
      const opt = opts.find((o) => o.value === value)
      return opt?.label ?? value
    }
    case "combobox":
    case "string":
    case "date":
      return typeof value === "string" && value.length > 0 ? value : null
    case "boolean":
      // Boolean uses variant change, not a suffix.
      return null
  }
  return null
}

// Whether the filter holds a non-default value. Used to decide whether
// the Clear button should appear (and could drive an "active" tint on
// individual filter buttons if we want one later).
function isFilterActive(spec: ReportFilterSpec, value: FilterValue): boolean {
  if (spec.kind === "date_range") {
    if (!isDateRange(value)) return false
    if (!value.start && !value.end) return false
    if (spec.default_days) {
      const def = daysAgoRange(spec.default_days)
      if (value.start === def.start && value.end === def.end) return false
    }
    return true
  }
  if (spec.kind === "boolean") return Boolean(value)
  if (spec.kind === "multi_select") {
    return Array.isArray(value) && value.length > 0
  }
  return typeof value === "string" && value.length > 0
}

function durableFilterValue(
  value: FilterValue,
): ReportScopeFilterValue | undefined {
  if (typeof value === "boolean") return value || undefined
  if (typeof value === "string") return value || undefined
  if (Array.isArray(value)) return value.length > 0 ? value : undefined
  return undefined
}

function buildViewConfig(
  filterSpec: ReportFilterSpec[],
  values: FilterValues,
  visibleColumnKeys: string[],
  orderedColumnKeys: string[],
  secondaryGroupBy?: string,
  fallbackDatePreset?: string,
  sort: ReportSortRule[] = [],
): ReportViewConfig {
  const filters: Record<string, ReportScopeFilterValue> = {}
  let groupBy: string | undefined
  let datePreset: string | undefined

  for (const spec of filterSpec) {
    const value = values[spec.key]
    if (spec.key === "group_by") {
      if (typeof value === "string" && value && value !== "none") {
        groupBy = value
      }
      continue
    }
    if (spec.key === "secondary_group_by") continue
    if (spec.kind === "date_range") {
      if (isDateRange(value) && reportDatePreset(value.preset)) {
        datePreset = value.preset
      }
      continue
    }
    // A standalone absolute date has no evergreen subscription/view meaning.
    // Only the shared relative date-range preset dialect is persisted.
    if (spec.kind === "date") continue

    const durable = durableFilterValue(value)
    if (durable !== undefined) filters[spec.key] = durable
  }
  if (!datePreset && reportDatePreset(fallbackDatePreset)) {
    datePreset = fallbackDatePreset
  }

  return {
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    ...(groupBy ? { group_by: groupBy } : {}),
    ...(secondaryGroupBy &&
    secondaryGroupBy !== "none" &&
    secondaryGroupBy !== groupBy
      ? { secondary_group_by: secondaryGroupBy }
      : {}),
    columns: {
      visible: visibleColumnKeys,
      order: orderedColumnKeys,
    },
    ...(sort.length > 0 ? { sort } : {}),
    ...(datePreset ? { date_range: { preset: datePreset } } : {}),
  }
}

const REPORT_LEGENDS: Record<string, string> = {
  current_inventory_and_transloads:
    "Positive values are inbound additions; negative values are outbound removals.",
}

export function buildReportDownloadHref(
  downloadPath: string,
  format: DownloadFormat,
  activeViewId: number | null,
  scopeParams: Record<string, string | string[]>,
  visibleColumnKeys: string[],
  sort: ReportSortRule[] = [],
): string {
  const params = new URLSearchParams()

  // Never rely on the user's mutable last-view pointer for an export. Two
  // open tabs may have different views selected; the download must remain
  // bound to the view visible in this tab (including an explicit Default).
  params.set(
    "view_id",
    activeViewId === null ? "default" : String(activeViewId),
  )
  Object.entries(scopeParams).forEach(([key, value]) => {
    // The preview supplies the canonical ordered selection below. Ignoring a
    // stale copy in scopeParams prevents duplicate or differently ordered
    // column parameters.
    if (key === "visible_columns") return
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(`${key}[]`, item))
    } else {
      params.set(key, value)
    }
  })
  visibleColumnKeys.forEach((key) => params.append("visible_columns[]", key))
  // Always send an explicit array. `[]` intentionally clears a saved view's
  // sort when the operator removes it before downloading.
  params.set("sort", JSON.stringify(sort))

  return `${downloadPath}.${format}?${params.toString()}`
}

export function bindReportViewToParams(
  activeViewId: number | null,
  params: Record<string, string | string[]>,
): Record<string, string | string[]> {
  return {
    ...params,
    // Never rely on the mutable last-view pointer for an in-tab navigation.
    // Another tab may have selected a different view since this page loaded.
    view_id: activeViewId === null ? "default" : String(activeViewId),
  }
}

export default function ReportShow({
  report,
  active_view,
  available_views,
  capabilities,
  send_history,
  org_users,
}: Props) {
  const routes = useOrgRoutes()
  const orgSlug = routes.orgSlug
  const today = todayStr()
  const legend = REPORT_LEGENDS[report.key]

  // Phase 2 — saved views state.
  // - columnManagerOpen: drives the right-side Sheet open/close
  // - saveModalOpen: drives the SaveViewModal
  // - liveColumnConfig: client-side preview of column visibility + order
  //   that diverges from the active_view's saved config when the user
  //   reorders or toggles. Compared against active_view.config for the
  //   dirty indicator. null = matches the saved view (clean).
  const [columnManagerOpen, setColumnManagerOpen] = useState(false)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState(active_view?.name ?? "")
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false)
  const [viewMutationError, setViewMutationError] = useState<string | null>(
    null,
  )
  const [expectedDeleteSubscriberCount, setExpectedDeleteSubscriberCount] =
    useState(0)
  const [savingView, setSavingView] = useState(false)
  const [staleView, setStaleView] = useState<ActiveSavedView | null>(null)
  const [staleRenameDraft, setStaleRenameDraft] = useState(false)
  const [deletedViewConflict, setDeletedViewConflict] = useState<
    "edit" | "delete" | null
  >(null)
  const [liveSubscriberCount, setLiveSubscriberCount] = useState<number | null>(
    null,
  )
  const [expectedViewLockVersion, setExpectedViewLockVersion] = useState(
    active_view?.lock_version ?? 0,
  )
  const columnCatalog = mergeReportColumnCatalog(
    report.columns,
    report.visible_columns,
  )
  // Columns the active view selected that this org cannot render. Carried
  // through both the save payload and the dirty baseline below: the first stops
  // an edit-then-save from deleting a selection the operator cannot see, the
  // second stops its absence from reading as an unsaved change. Wiring only one
  // half is worse than neither, so both go through withSuppressedColumns.
  const suppressedColumnKeys = report.suppressed_columns ?? []
  const initialVisible = withSuppressedColumns(
    report.visible_columns.map((c) => c.key),
    suppressedColumnKeys,
  )
  const initialOrder =
    active_view?.config.columns?.order ??
    active_view?.config.columns?.visible ??
    // Same reason as defaultViewConfig below: an order list built from the
    // catalog would not match one built from the rendered set.
    renderableColumnKeys(report.columns)
  const initialSort = active_view?.config.sort ?? report.sort ?? []
  const [liveColumnConfig, setLiveColumnConfig] = useState<{
    visible: string[]
    order: string[]
    sort: ReportSortRule[]
  } | null>(null)

  // Reset live config when the active view changes (e.g. user picks a
  // different view via the picker). Without this, switching views would
  // carry the previous view's unsaved edits to the next one.
  useEffect(() => {
    setLiveColumnConfig(null)
    setStaleView(null)
    setStaleRenameDraft(false)
    setDeletedViewConflict(null)
    setLiveSubscriberCount(null)
  }, [active_view?.id])

  useEffect(() => {
    if (!staleRenameDraft) setRenameValue(active_view?.name ?? "")
  }, [active_view?.name, staleRenameDraft])

  useEffect(() => {
    setExpectedViewLockVersion(active_view?.lock_version ?? 0)
  }, [active_view?.id, active_view?.lock_version])

  // One values object keyed by filter.key. Initialized from the server's
  // filter_values (which already passed through `params.permit` and so
  // matches the spec's permitted keys). date_range filters read their
  // two halves out of filter_values via start_key / end_key.
  const [values, setValues] = useState<FilterValues>(() => {
    const init: FilterValues = {}
    const query =
      typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.search)
    report.filter_spec.forEach((f) => {
      const explicitDateOverride =
        f.kind === "date_range" &&
        Boolean(
          query?.has(f.start_key ?? "start_date") ||
          query?.has(f.end_key ?? "end_date"),
        )
      init[f.key] = coerceInitial(
        f,
        report.filter_values[f.key],
        report.filter_values,
        active_view?.config.date_range?.preset,
        explicitDateOverride,
        query?.get("date_preset"),
      )
    })
    return init
  })

  const groupedColumnKeys = [values.group_by, values.secondary_group_by].filter(
    (value): value is string =>
      typeof value === "string" && value.length > 0 && value !== "none",
  )
  const groupedColumnKeySet = new Set(groupedColumnKeys)

  // Effective visible columns: client-side override (when user reordered or
  // toggled) OR the controller-supplied visible_columns. Group axes remain
  // hidden exactly as they are in server-rendered downloads and emails.
  const effectiveColumns: ReportColumn[] = (() => {
    if (!liveColumnConfig) return report.visible_columns
    const byKey = new Map(columnCatalog.map((c) => [c.key, c]))
    const visibleSet = new Set(liveColumnConfig.visible)
    return liveColumnConfig.order
      .map((key) => byKey.get(key))
      .filter(
        (column): column is ReportColumn =>
          column !== undefined &&
          visibleSet.has(column.key) &&
          !groupedColumnKeySet.has(column.key),
      )
  })()
  const currentSort = sortRulesWithoutGrouping(
    liveColumnConfig?.sort ?? initialSort,
    groupedColumnKeys,
  )
  const effectiveRows = sortReportRows(report.rows, currentSort)

  const setValue = (key: string, v: FilterValue) => {
    setValues((prev) => ({ ...prev, [key]: v }))
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Reports", href: routes.reportsPath() },
    { title: report.title, href: "" },
  ]

  // Build the URL params from current values, dropping empties. A
  // date_range filter expands to two URL params (start_key + end_key);
  // every other filter is a single key.
  //
  // Accepts an optional `override` so callers can pass a freshly-edited
  // value without waiting for `setValues` to flush — React state writes
  // are async and toUrlParams would otherwise serialize stale state.
  const toUrlParams = (
    override?: {
      key: string
      value: FilterValue
    },
    includeClearMarkers = true,
  ): Record<string, string | string[]> => {
    const out: Record<string, string | string[]> = {}
    report.filter_spec.forEach((f) => {
      const v =
        override && override.key === f.key ? override.value : values[f.key]
      if (f.kind === "boolean") {
        if (v) out[f.key] = "true"
      } else if (f.kind === "multi_select") {
        const arr = (v as string[]) ?? []
        if (arr.length > 0) out[f.key] = arr
      } else if (f.kind === "date_range") {
        if (isDateRange(v)) {
          const startKey = f.start_key ?? "start_date"
          const endKey = f.end_key ?? "end_date"
          if (v.start) out[startKey] = v.start
          if (v.end) out[endKey] = v.end
          if (reportDatePreset(v.preset)) out.date_preset = v.preset as string
        }
      } else if (typeof v === "string" && v.length > 0) {
        out[f.key] = v
      }
    })
    if (includeClearMarkers && active_view) {
      const cleared = Object.keys(active_view.config.filters ?? {}).filter(
        (key) => {
          const current = override?.key === key ? override.value : values[key]
          return durableFilterValue(current) === undefined
        },
      )
      const dateValue = report.filter_spec
        .filter((spec) => spec.kind === "date_range")
        .map((spec) =>
          override?.key === spec.key ? override.value : values[spec.key],
        )
        .find(isDateRange)
      if (
        active_view.config.date_range &&
        (!dateValue || (!dateValue.start && !dateValue.end))
      ) {
        cleared.push("date_range")
      }
      const groupValue =
        override?.key === "group_by" ? override.value : values.group_by
      if (
        active_view.config.group_by &&
        (groupValue === "none" || !groupValue)
      ) {
        cleared.push("group_by")
        delete out.group_by
      }
      if (cleared.length > 0) out.clear_filters = [...new Set(cleared)]
    }
    return out
  }

  const secondaryGroupBy =
    typeof values.secondary_group_by === "string"
      ? values.secondary_group_by
      : typeof report.filter_values.secondary_group_by === "string"
        ? report.filter_values.secondary_group_by
        : undefined
  const dateRangeSpec = report.filter_spec.find(
    (spec) => spec.kind === "date_range",
  )
  const durableDateFallback = reportDatePresetForTrailingDays(
    dateRangeSpec?.default_days,
  )?.key
  const durableDateFallbackLabel = reportDatePreset(durableDateFallback)?.label
  const currentColumnOrder = liveColumnConfig?.order ?? initialOrder
  const fallbackVisibleColumn =
    report.visible_columns[0]?.key ?? report.columns[0]?.key
  const durableVisibleColumnKeys =
    effectiveColumns.length > 0
      ? effectiveColumns.map((column) => column.key)
      : fallbackVisibleColumn
        ? [fallbackVisibleColumn]
        : []
  // What a Save WRITES, as opposed to what the table shows.
  //
  // When the operator has not touched the Column Manager, the saved view's own
  // columns config IS the answer — reuse it verbatim. Deriving the payload from
  // the RENDERED set instead goes wrong two ways for a column this organization
  // cannot render: it loses that column's saved position (the rendered order
  // never contained it), and when suppression fell back to the defaults it
  // widens a deliberately narrow selection into the whole report.
  //
  // Once columns ARE edited, fall back to the rendered set plus the suppressed
  // keys. That union is unconditional: a suppressed column is absent from
  // `report.columns`, so the Column Manager cannot list it and the operator
  // cannot uncheck it — toggling an unrelated column must not delete it. On a
  // tons org `suppressedColumnKeys` is empty, so all of this is a no-op.
  //
  // Normalized to carry BOTH keys: buildViewConfig always emits `order`, so a
  // view stored with `visible` alone would otherwise differ from its own
  // baseline on shape and open as "Modified from" before anything was touched.
  const saved = liveColumnConfig ? null : active_view?.config.columns
  const savedColumnConfig = saved
    ? { visible: saved.visible, order: saved.order ?? saved.visible }
    : null
  const persistedVisibleColumnKeys =
    savedColumnConfig?.visible ??
    withSuppressedColumns(durableVisibleColumnKeys, suppressedColumnKeys)
  const persistedColumnOrder =
    savedColumnConfig?.order ??
    withSuppressedColumns(currentColumnOrder, suppressedColumnKeys)
  const currentViewConfig = buildViewConfig(
    report.filter_spec,
    values,
    // Compared against a baseline that also carries the suppressed keys, so a
    // column this org cannot render never reads as an unsaved change.
    persistedVisibleColumnKeys,
    persistedColumnOrder,
    secondaryGroupBy,
    durableDateFallback,
    currentSort,
  )
  const hasCustomAbsoluteDate = report.filter_spec.some((spec) => {
    const value = values[spec.key]
    return (
      spec.kind === "date_range" &&
      isDateRange(value) &&
      Boolean(value.start || value.end) &&
      !reportDatePreset(value.preset)
    )
  })
  const defaultValues: FilterValues = {}
  report.filter_spec.forEach((spec) => {
    defaultValues[spec.key] = coerceInitial(spec, undefined, {})
  })
  // The Default baseline must be the columns the server would RENDER, not the
  // whole catalog. `columns` now carries opt-in companions that `visible_columns`
  // deliberately omits, so comparing against the catalog made every tons/tonnes
  // org open permanently "Modified from Default" — which also tripped
  // SubscriptionsDialog's `!isDirty` gate and blocked subscribing outright.
  const defaultColumnKeys = renderableColumnKeys(report.columns)
  const defaultViewConfig = buildViewConfig(
    report.filter_spec,
    defaultValues,
    defaultColumnKeys,
    defaultColumnKeys,
  )
  const activeBaselineFilters = active_view
    ? {
        ...(defaultViewConfig.filters ?? {}),
        ...(active_view.config.filters ?? {}),
      }
    : {}
  const activeBaselineConfig: ReportViewConfig | null = active_view
    ? {
        ...defaultViewConfig,
        ...active_view.config,
        // Match buildViewConfig's canonical shape: an empty filter set is
        // absent, not an always-dirty `filters: {}` placeholder.
        filters:
          Object.keys(activeBaselineFilters).length > 0
            ? activeBaselineFilters
            : undefined,
        // Compare against the columns the server could actually render. A
        // temporarily absent dynamic column should not make a saved view look
        // edited before the user changes anything.
        columns: savedColumnConfig ?? {
          visible: initialVisible,
          order: initialOrder,
        },
        // Empty sort is the canonical default and should not make a legacy
        // view look modified immediately after opening it.
        sort:
          active_view.config.sort && active_view.config.sort.length > 0
            ? active_view.config.sort
            : undefined,
      }
    : null
  const isDirty =
    hasCustomAbsoluteDate ||
    reportConfigFingerprint(currentViewConfig) !==
      reportConfigFingerprint(activeBaselineConfig ?? defaultViewConfig)
  const activeViewReady = !active_view || active_view.status === "ready"
  const canManageActiveViewSubscribers = active_view
    ? active_view.capabilities.can_manage_subscribers
    : (capabilities.can_manage_subscribers ?? capabilities.can_create_view)
  const activeSubscriberCount =
    liveSubscriberCount ??
    report.active_subscriber_count ??
    active_view?.subscriber_count ??
    0

  const clearFilters = () => {
    const cleared: FilterValues = {}
    report.filter_spec.forEach((f) => {
      cleared[f.key] = coerceInitial(f, undefined, {})
    })
    setValues(cleared)
    const clearMarkers = active_view
      ? [
          ...Object.keys(active_view.config.filters ?? {}),
          ...(active_view.config.group_by ? ["group_by"] : []),
          ...(active_view.config.secondary_group_by
            ? ["secondary_group_by"]
            : []),
          ...(active_view.config.date_range ? ["date_range"] : []),
        ]
      : []
    router.get(
      routes.reportPath(report.key),
      active_view
        ? {
            view_id: String(active_view.id),
            ...(clearMarkers.length > 0
              ? { clear_filters: [...new Set(clearMarkers)] }
              : {}),
          }
        : { view_id: "default" },
      { preserveState: false },
    )
  }

  // One shared debounce timer. String filters (typed substrings like
  // Text filters wait 400ms before firing so each keystroke
  // doesn't trigger a request. Everything else fires immediately.
  //
  // `preserveState: true` keeps the popover open across rapid picks so
  // the user can click multiple checkboxes (or tweak both from/to
  // dates) without the popover resetting on every navigation.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cancel any pending debounced navigation when the page unmounts so a
  // stale router.get doesn't fire against an already-gone component.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const applyChange = (f: ReportFilterSpec, v: FilterValue) => {
    const normalizedValue =
      f.kind === "date_range" &&
      isDateRange(v) &&
      !v.start &&
      !v.end &&
      f.default_days
        ? daysAgoRange(f.default_days)
        : v
    setValue(f.key, normalizedValue)
    const params = toUrlParams({ key: f.key, value: normalizedValue })
    const delay = f.kind === "string" ? 400 : 0
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      router.get(
        routes.reportPath(report.key),
        bindReportViewToParams(active_view?.id ?? null, params),
        {
          preserveState: true,
          preserveScroll: true,
        },
      )
    }, delay)
  }

  const resetViewChanges = () => {
    router.visit(
      `${routes.reportPath(report.key)}?view_id=${active_view?.id ?? "default"}`,
      { preserveScroll: true },
    )
  }

  const parseMutationError = async (
    response: Response,
    operation: "edit" | "delete" = "edit",
  ) => {
    const body = (await response.json().catch(() => ({}))) as {
      errors?: string[]
      error?: string | { code?: string; message?: string }
      current_view?: ActiveSavedView | null
      subscriber_count?: number
    }
    const conflict = reportViewConflictKind(
      response.status,
      typeof body.error === "object" ? body.error?.code : undefined,
      Boolean(body.current_view),
    )
    if (conflict === "stale" && body.current_view) {
      if (operation === "delete") {
        setExpectedViewLockVersion(body.current_view.lock_version)
        setExpectedDeleteSubscriberCount(
          body.current_view.subscriber_count ?? 0,
        )
        return "This organization view changed in another tab. Review the updated impact and confirm again."
      }
      setStaleView(body.current_view)
      return "This organization view changed in another tab."
    }
    if (conflict === "deleted") {
      setDeletedViewConflict(operation)
      return "This organization view was deleted in another tab."
    }
    if (
      response.status === 409 &&
      typeof body.error === "object" &&
      body.error?.code === "subscriber_count_changed"
    ) {
      const count = body.subscriber_count ?? 0
      setExpectedDeleteSubscriberCount(count)
      return `The subscriber count changed to ${count}. Review the updated impact and confirm again.`
    }
    if (typeof body.error === "string") return body.error
    if (body.error?.message) return body.error.message
    return body.errors?.join(", ") ?? "The view could not be updated."
  }

  const saveChangesToActiveView = async () => {
    if (!active_view) return
    if (hasCustomAbsoluteDate) {
      toast.warning(
        "Choose a Relative window before saving changes to this view.",
      )
      return
    }
    setSavingView(true)
    setViewMutationError(null)
    const url = `${routes.reportPath(report.key)}/views/${active_view.id}`
    try {
      const res = await csrfFetch(url, {
        method: "PATCH",
        body: JSON.stringify({
          view: {
            config: currentViewConfig,
            lock_version: expectedViewLockVersion,
          },
        }),
      })
      if (res.ok) {
        toast.success("View updated")
        router.visit(
          `${routes.reportPath(report.key)}?view_id=${active_view.id}`,
          { preserveScroll: true },
        )
      } else {
        const message = await parseMutationError(res)
        setViewMutationError(message)
        if (res.status !== 409) toast.error(message)
      }
    } catch {
      toast.error("Network error — try again.")
    } finally {
      setSavingView(false)
    }
  }

  const renameActiveView = async () => {
    if (!active_view || !renameValue.trim()) return
    setSavingView(true)
    setViewMutationError(null)
    try {
      const response = await csrfFetch(
        `${routes.reportPath(report.key)}/views/${active_view.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            view: {
              name: renameValue.trim(),
              lock_version: expectedViewLockVersion,
            },
          }),
        },
      )
      if (!response.ok) {
        const message = await parseMutationError(response)
        setViewMutationError(message)
        if (response.status === 409) {
          setStaleRenameDraft(true)
          setRenameOpen(false)
        }
        return
      }
      setStaleRenameDraft(false)
      setRenameOpen(false)
      toast.success("Organization view renamed.")
      router.reload({ only: ["active_view", "available_views"] })
    } catch {
      setViewMutationError("Network error — try again.")
    } finally {
      setSavingView(false)
    }
  }

  const deleteActiveView = async () => {
    if (!active_view) return
    setSavingView(true)
    setViewMutationError(null)
    try {
      const response = await csrfFetch(
        `${routes.reportPath(report.key)}/views/${active_view.id}`,
        {
          method: "DELETE",
          body: JSON.stringify({
            view: {
              lock_version: expectedViewLockVersion,
            },
            expected_subscriber_count: expectedDeleteSubscriberCount,
          }),
        },
      )
      if (!response.ok) {
        const message = await parseMutationError(response, "delete")
        setViewMutationError(message)
        if (message.startsWith("This organization view was deleted")) {
          setDeleteOpen(false)
        }
        return
      }
      toast.success(`Deleted “${active_view.name}”.`)
      setDeleteOpen(false)
      router.visit(`${routes.reportPath(report.key)}?view_id=default`, {
        preserveScroll: true,
      })
    } catch {
      setViewMutationError("Network error — try again.")
    } finally {
      setSavingView(false)
    }
  }

  const downloadHref = (format: DownloadFormat) => {
    return buildReportDownloadHref(
      routes.downloadReportPath(report.key),
      format,
      active_view?.id ?? null,
      toUrlParams(),
      durableVisibleColumnKeys,
      currentSort,
    )
  }

  // Mirrors ReportsController#download's default base filename so the
  // dialog's input opens pre-populated with the same name the server
  // would have used without a `?filename=` override.
  const defaultDownloadFilename = `${report.key}_${new Date().toISOString().slice(0, 10)}`

  // The on-screen scope forwarded to Send so the emailed report matches the
  // view (filters + grouping + visible columns). `values`/effectiveColumns
  // already fold in the active saved view, so the email reflects it too.
  const sendGroupBy = currentViewConfig.group_by
  const sendSecondaryGroupBy = currentViewConfig.secondary_group_by
  const sendScopeParams: Record<string, string | string[] | undefined> = {
    ...toUrlParams(undefined, false),
    group_by: sendGroupBy,
    secondary_group_by: sendSecondaryGroupBy,
    visible_columns: durableVisibleColumnKeys,
    sort: JSON.stringify(currentSort),
  }
  const screenScopeSummaryParts = report.filter_spec.flatMap((spec) => {
    const summary = filterSummary(spec, values[spec.key])
    if (!summary) return []
    return [
      spec.key === "group_by"
        ? `Grouped by ${summary}`
        : `${spec.label}: ${summary}`,
    ]
  })
  screenScopeSummaryParts.push(
    `${durableVisibleColumnKeys.length} visible column${durableVisibleColumnKeys.length === 1 ? "" : "s"}`,
  )
  if (currentSort.length > 0) {
    const labels = new Map(
      columnCatalog.map((column) => [column.key, column.label]),
    )
    screenScopeSummaryParts.push(
      `Sorted by ${currentSort
        .map(
          (rule, index) =>
            `${index > 0 ? "then " : ""}${labels.get(rule.key) ?? rule.key} ${
              rule.dir === "asc" ? "ascending" : "descending"
            }`,
        )
        .join(", ")}`,
    )
  }
  const sendScopeSummary = screenScopeSummaryParts.join(" · ")

  const hasFilters = report.filter_spec.length > 0
  // Show Clear only when at least one filter holds a non-default value,
  // so the default-90-day window alone doesn't surface a Clear button
  // that effectively does nothing.
  const hasActiveFilters = report.filter_spec.some((f) =>
    isFilterActive(f, values[f.key]),
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={report.title} />
      <div className="space-y-6 p-4 sm:p-6">
        <header className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold">{report.title}</h1>
              <p className="text-muted-foreground text-sm">
                {report.description}
              </p>
              {legend && (
                <p className="text-muted-foreground mt-1 text-xs italic">
                  {legend}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {activeViewReady ? (
                <>
                  <DownloadReportDialog
                    defaultFilename={defaultDownloadFilename}
                    buildHref={downloadHref}
                    canSplit={report.can_split_by_customer}
                  />
                  {orgSlug && capabilities.can_send_report && (
                    <SendReportDialog
                      reportKey={report.key}
                      reportTitle={report.title}
                      orgSlug={orgSlug}
                      orgUsers={org_users}
                      savedRecipients={report.saved_recipients}
                      canReenableSavedRecipients={
                        capabilities.can_manage_saved_recipients
                      }
                      canManageList={capabilities.can_manage_saved_recipients}
                      scopeParams={sendScopeParams}
                      scopeSummary={sendScopeSummary}
                    />
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" className="h-11" disabled>
                    Download
                  </Button>
                  <Button variant="outline" className="h-11" disabled>
                    Send
                  </Button>
                </>
              )}
            </div>
          </div>

          {capabilities.can_use_org_views && (
            <div className="bg-card rounded-lg border p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <ViewPicker
                    views={available_views}
                    activeView={active_view}
                    reportPath={routes.reportPath(report.key)}
                    canCreate={capabilities.can_create_view}
                    hasUnsavedChanges={isDirty}
                    canSaveCurrentView={!hasCustomAbsoluteDate}
                    saveDisabledReason={
                      hasCustomAbsoluteDate
                        ? "Choose a relative date window before saving an organization view."
                        : undefined
                    }
                    onSaveCurrentView={() => setSaveModalOpen(true)}
                  />
                  <ColumnManagerTrigger
                    onClick={() => setColumnManagerOpen(true)}
                  />
                  {report.subscribable && (
                    <SubscriptionsDialog
                      reportKey={report.key}
                      reportTitle={report.title}
                      activeView={active_view}
                      mySubscription={report.my_subscription_for_active_view}
                      subscriberCount={report.active_subscriber_count}
                      canSubscribeSelf={capabilities.can_subscribe_self}
                      canManageSubscribers={canManageActiveViewSubscribers}
                      canCreateSubscriptions={activeViewReady}
                      orgUsers={org_users}
                      isDirty={isDirty}
                      onSubscriberCountChange={setLiveSubscriberCount}
                    />
                  )}
                  {active_view &&
                    (active_view.capabilities.can_update ||
                      active_view.capabilities.can_delete) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="size-11"
                            aria-label={`Actions for ${active_view.name}`}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {active_view.capabilities.can_update && (
                            <DropdownMenuItem
                              onSelect={() => {
                                setViewMutationError(null)
                                if (!staleRenameDraft) {
                                  setRenameValue(active_view.name)
                                }
                                setRenameOpen(true)
                              }}
                            >
                              <Pencil className="size-4" /> Rename
                            </DropdownMenuItem>
                          )}
                          {active_view.capabilities.can_update &&
                            active_view.capabilities.can_delete && (
                              <DropdownMenuSeparator />
                            )}
                          {active_view.capabilities.can_delete && (
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => {
                                setViewMutationError(null)
                                setExpectedDeleteSubscriberCount(
                                  activeSubscriberCount,
                                )
                                setDeleteOpen(true)
                              }}
                            >
                              <Trash2 className="size-4" /> Delete view
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                </div>

                {isDirty && (
                  <div
                    className="flex flex-col gap-2 sm:flex-row sm:items-center"
                    aria-live="polite"
                  >
                    <span className="text-sm font-medium text-amber-800">
                      {active_view
                        ? `Modified from “${active_view.name}”`
                        : "Modified from Default"}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="ghost"
                        className="h-11"
                        onClick={resetViewChanges}
                      >
                        Reset
                      </Button>
                      {active_view?.capabilities.can_update && (
                        <Button
                          className="h-11"
                          disabled={hasCustomAbsoluteDate || savingView}
                          onClick={() =>
                            activeSubscriberCount > 0
                              ? setUpdateConfirmOpen(true)
                              : void saveChangesToActiveView()
                          }
                        >
                          Update organization view
                        </Button>
                      )}
                      {!active_view && capabilities.can_create_view && (
                        <Button
                          className="h-11"
                          disabled={hasCustomAbsoluteDate}
                          onClick={() => setSaveModalOpen(true)}
                        >
                          Save as organization view
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {hasCustomAbsoluteDate && (
                <p className="mt-2 text-xs text-amber-800">
                  Choose a relative date window before saving or subscribing.
                </p>
              )}
            </div>
          )}
          {!capabilities.can_use_org_views && (
            <div className="bg-card rounded-lg border p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex h-11 items-center rounded-md border px-3 text-sm font-medium">
                    Default
                  </span>
                  <ColumnManagerTrigger
                    onClick={() => setColumnManagerOpen(true)}
                  />
                  {report.subscribable && capabilities.can_subscribe_self && (
                    <SubscriptionsDialog
                      reportKey={report.key}
                      reportTitle={report.title}
                      activeView={null}
                      mySubscription={report.my_subscription_for_active_view}
                      subscriberCount={null}
                      canSubscribeSelf
                      canManageSubscribers={false}
                      canCreateSubscriptions={activeViewReady}
                      orgUsers={[]}
                      isDirty={isDirty}
                      onSubscriberCountChange={setLiveSubscriberCount}
                    />
                  )}
                </div>
                {isDirty && (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    aria-live="polite"
                  >
                    <span className="text-sm font-medium text-amber-800">
                      Modified from Default
                    </span>
                    <Button
                      variant="ghost"
                      className="h-11"
                      onClick={resetViewChanges}
                    >
                      Reset
                    </Button>
                  </div>
                )}
              </div>
              {hasCustomAbsoluteDate && (
                <p className="mt-2 text-xs text-amber-800">
                  Choose a relative date window before subscribing.
                </p>
              )}
            </div>
          )}
        </header>

        {!activeViewReady && active_view && (
          <div
            role="alert"
            className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950"
          >
            <h2 className="font-medium">This view needs review</h2>
            <p className="mt-1 text-sm">
              Download, send, and subscribe are unavailable until an editor
              fixes the view.
            </p>
            {active_view.issues.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {active_view.issues.map((issue, index) => (
                  <li key={`${index}-${reportViewIssueMessage(issue)}`}>
                    {reportViewIssueMessage(issue)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {hasFilters && activeViewReady && (
          // Items-end so each filter's control sits flush at the bottom
          // (labels can wrap to two lines on narrow viewports without
          // throwing the controls out of alignment). gap-x-3 between
          // filters, gap-y-4 between rows when the bar wraps.
          <div className="flex flex-wrap items-end gap-x-3 gap-y-4">
            {report.filter_spec.map((f) => (
              <FilterWidget
                key={f.key}
                spec={f}
                value={values[f.key]}
                today={today}
                onChange={(v) => applyChange(f, v)}
              />
            ))}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground h-11 self-end"
              >
                Clear
              </Button>
            )}
          </div>
        )}

        <div className="bg-card overflow-hidden rounded-md border">
          <Table>
            <TableHeader className="bg-background sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
              <TableRow className="border-b">
                {effectiveColumns.map((c) => (
                  <TableHead
                    key={c.key}
                    className={cn(
                      "text-muted-foreground h-11 px-3 text-[11px] font-medium tracking-wide uppercase",
                      isNumericFormat(c.format) && "text-right",
                    )}
                  >
                    {c.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {effectiveRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={effectiveColumns.length}
                    className="text-muted-foreground py-12 text-center"
                  >
                    No data.
                  </TableCell>
                </TableRow>
              ) : (
                effectiveRows.map((row, i) => {
                  const visibleCols = effectiveColumns

                  // Sentinel: __grand_total — strongest visual emphasis,
                  // dark border-top + sky-200 bg per Locked Visual Spec.
                  if (row[GRAND_TOTAL_KEY]) {
                    const firstNumericIdx = visibleCols.findIndex((c) =>
                      isNumericFormat(c.format),
                    )
                    const splitIdx =
                      firstNumericIdx === -1
                        ? visibleCols.length
                        : firstNumericIdx
                    const hasGrandLabelSlot = splitIdx > 0
                    return (
                      <TableRow
                        key={`grand-${i}`}
                        className="border-t-2 border-sky-600 bg-sky-100 dark:bg-sky-900/50"
                      >
                        {hasGrandLabelSlot && (
                          <TableCell
                            colSpan={splitIdx}
                            className="px-3 py-3 text-right text-base font-bold"
                          >
                            Grand Total
                          </TableCell>
                        )}
                        {visibleCols
                          .slice(hasGrandLabelSlot ? splitIdx : 0)
                          .map((c, valueIndex) => (
                            <TableCell
                              key={c.key}
                              className={cn(
                                "px-3 py-3 text-base font-bold",
                                isNumericFormat(c.format)
                                  ? "text-right tabular-nums"
                                  : "",
                              )}
                            >
                              {!hasGrandLabelSlot && valueIndex === 0
                                ? `Grand Total: ${formatCell(row[c.key], c.format, c.signed, c.unit)}`
                                : isNumericFormat(c.format)
                                  ? formatCell(
                                      row[c.key],
                                      c.format,
                                      c.signed,
                                      c.unit,
                                    )
                                  : ""}
                            </TableCell>
                          ))}
                      </TableRow>
                    )
                  }

                  // Sentinel: __group_subtotal — sky-100 bg, bold right
                  // for numeric, blank for non-numeric (per spec).
                  if (row[GROUP_SUBTOTAL_KEY]) {
                    const visibleCols2 = visibleCols
                    const firstNumericIdx2 = visibleCols2.findIndex((c) =>
                      isNumericFormat(c.format),
                    )
                    const splitIdx2 =
                      firstNumericIdx2 === -1
                        ? visibleCols2.length
                        : firstNumericIdx2
                    const hasSubtotalLabelSlot = splitIdx2 > 0
                    const subtotalLabel =
                      `${row[GROUP_SUBTOTAL_LABEL_KEY] ?? ""} subtotal`.trim()
                    const depth =
                      typeof row[GROUP_HEADER_DEPTH_KEY] === "number"
                        ? (row[GROUP_HEADER_DEPTH_KEY] as number)
                        : 0
                    const indent =
                      depth === 0 ? "" : depth === 1 ? "pl-8" : "pl-12"
                    return (
                      <TableRow
                        key={`subtot-${i}`}
                        className="bg-sky-50 dark:bg-sky-900/30"
                      >
                        {hasSubtotalLabelSlot && (
                          <TableCell
                            colSpan={splitIdx2}
                            className={cn(
                              "px-3 py-2.5 text-right text-sm font-semibold",
                              indent,
                            )}
                          >
                            {subtotalLabel}
                          </TableCell>
                        )}
                        {visibleCols2
                          .slice(hasSubtotalLabelSlot ? splitIdx2 : 0)
                          .map((c, valueIndex) => (
                            <TableCell
                              key={c.key}
                              className={cn(
                                "px-3 py-2.5 text-sm font-semibold",
                                isNumericFormat(c.format)
                                  ? "text-right tabular-nums"
                                  : "",
                              )}
                            >
                              {!hasSubtotalLabelSlot && valueIndex === 0
                                ? `${subtotalLabel}: ${formatCell(row[c.key], c.format, c.signed, c.unit)}`
                                : isNumericFormat(c.format)
                                  ? formatCell(
                                      row[c.key],
                                      c.format,
                                      c.signed,
                                      c.unit,
                                    )
                                  : ""}
                            </TableCell>
                          ))}
                      </TableRow>
                    )
                  }

                  // Sentinel: __group_header
                  const groupLabel = row[GROUP_HEADER_KEY]
                  if (groupLabel !== undefined && groupLabel !== null) {
                    // depth 0 = outer group, depth 1+ = nested group.
                    // Inner levels get a lighter band + indent so the nesting
                    // is visually obvious without extra columns. Tailwind
                    // purges class names it can't see statically, so the
                    // indent uses a small static lookup, not interpolation.
                    const depthRaw = row[GROUP_HEADER_DEPTH_KEY]
                    const depth =
                      typeof depthRaw === "number" && depthRaw > 0
                        ? depthRaw
                        : 0
                    // slate-100 / slate-50 per Locked Visual Spec (was muted/40).
                    const rowClass =
                      depth === 0
                        ? "bg-slate-100 dark:bg-slate-800"
                        : "bg-slate-50 dark:bg-slate-800/60"
                    const indent =
                      depth === 0 ? "" : depth === 1 ? "pl-8" : "pl-12"
                    const cellClass =
                      depth === 0
                        ? "px-3 py-2.5 text-base font-semibold"
                        : `px-3 py-2.5 text-sm font-medium ${indent}`
                    return (
                      <TableRow
                        key={`group-${i}-${String(groupLabel)}`}
                        className={cn("border-b-0", rowClass)}
                      >
                        <TableCell
                          colSpan={visibleCols.length}
                          className={cellClass}
                        >
                          {String(groupLabel)}
                        </TableCell>
                      </TableRow>
                    )
                  }
                  return (
                    <TableRow
                      key={`row-${i}`}
                      className="border-b-0 odd:bg-slate-50/40 dark:odd:bg-slate-900/20"
                    >
                      {visibleCols.map((c, idx) => (
                        <TableCell
                          key={c.key}
                          className={cn(
                            "px-3 py-3",
                            idx === 0 && "text-foreground font-medium",
                            isNumericFormat(c.format) &&
                              "text-right tabular-nums",
                          )}
                        >
                          {formatCell(row[c.key], c.format, c.signed, c.unit)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>

        <SendHistorySection entries={send_history} />
      </div>

      <ColumnManager
        allColumns={columnCatalog}
        initialVisibleKeys={initialVisible}
        initialOrderKeys={initialOrder}
        initialSort={initialSort}
        disabledKeys={groupedColumnKeys}
        open={columnManagerOpen}
        onOpenChange={setColumnManagerOpen}
        onConfigChange={(next) => setLiveColumnConfig(next)}
      />
      <SaveViewModal
        open={saveModalOpen}
        onOpenChange={setSaveModalOpen}
        config={currentViewConfig}
        existingViews={available_views}
        createPath={`${routes.reportPath(report.key)}/views`}
        buildSuccessPath={(id) =>
          `${routes.reportPath(report.key)}?view_id=${id}&remember_view=1`
        }
        captureWarning={
          hasCustomAbsoluteDate
            ? `Custom calendar dates are not durable. This view will save the other filters and use ${
                durableDateFallbackLabel
                  ? `“${durableDateFallbackLabel}”`
                  : "the report default date window"
              }; choose a Relative window to preserve different date semantics.`
            : undefined
        }
      />

      <Dialog
        open={renameOpen}
        onOpenChange={(next) => {
          setRenameOpen(next)
          if (!next) {
            setStaleRenameDraft(false)
            setRenameValue(active_view?.name ?? "")
          }
        }}
      >
        <DialogContent className="**:data-[slot=dialog-close]:flex **:data-[slot=dialog-close]:size-11 **:data-[slot=dialog-close]:items-center **:data-[slot=dialog-close]:justify-center">
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault()
              void renameActiveView()
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename organization view</DialogTitle>
              <DialogDescription>
                The new name is visible to everyone in the organization.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="rename-report-view">View name</Label>
              <Input
                id="rename-report-view"
                value={renameValue}
                onChange={(event) => setRenameValue(event.target.value)}
                maxLength={80}
                className="h-11"
                autoFocus
              />
              {viewMutationError && (
                <p role="alert" className="text-destructive text-sm">
                  {viewMutationError}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                className="h-11"
                onClick={() => {
                  setRenameOpen(false)
                  setStaleRenameDraft(false)
                  setRenameValue(active_view?.name ?? "")
                }}
                disabled={savingView}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-11"
                disabled={savingView || !renameValue.trim()}
              >
                {savingView && <Loader2 className="mr-2 size-4 animate-spin" />}
                Rename view
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={updateConfirmOpen} onOpenChange={setUpdateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update “{active_view?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Future deliveries to {activeSubscriberCount} subscriber
              {activeSubscriberCount === 1 ? "" : "s"} will use these changes.
              Already queued emails keep their saved copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="h-11"
              onClick={() => void saveChangesToActiveView()}
            >
              Update organization view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{active_view?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This cancels {expectedDeleteSubscriberCount} subscription
              {expectedDeleteSubscriberCount === 1 ? "" : "s"} and any queued
              emails for this view. Sent-email history is retained. This cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {viewMutationError && (
            <p role="alert" className="text-destructive text-sm">
              {viewMutationError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11">Keep view</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              className="h-11"
              disabled={savingView}
              onClick={(event) => {
                event.preventDefault()
                void deleteActiveView()
              }}
            >
              Delete view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(staleView)}
        onOpenChange={(next) => !next && setStaleView(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This view changed elsewhere</AlertDialogTitle>
            <AlertDialogDescription>
              Your edits are still on this screen. Choose whether to keep
              working, save them as a new organization view, or load the latest
              saved version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="h-11"
              onClick={() => {
                if (staleView) {
                  setExpectedViewLockVersion(staleView.lock_version)
                }
                if (staleRenameDraft) setRenameOpen(true)
              }}
            >
              Keep editing
            </AlertDialogCancel>
            <Button
              variant="outline"
              className="h-11"
              onClick={() => {
                setStaleView(null)
                setRenameOpen(false)
                setStaleRenameDraft(false)
                setSaveModalOpen(true)
              }}
            >
              Save as new view
            </Button>
            <AlertDialogAction
              className="h-11"
              onClick={() => {
                setStaleRenameDraft(false)
                resetViewChanges()
              }}
            >
              Reload latest
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deletedViewConflict !== null}
        onOpenChange={(next) => !next && setDeletedViewConflict(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This view was deleted elsewhere</AlertDialogTitle>
            <AlertDialogDescription>
              {deletedViewConflict === "edit"
                ? "Your report changes are still on this screen. Save them as a new organization view, or return to Default."
                : "This organization view no longer exists. Return to Default to refresh the report."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deletedViewConflict === "edit" && (
              <Button
                variant="outline"
                className="h-11"
                onClick={() => {
                  setDeletedViewConflict(null)
                  setRenameOpen(false)
                  setSaveModalOpen(true)
                }}
              >
                Save as new view
              </Button>
            )}
            <AlertDialogAction
              className="h-11"
              onClick={() => {
                setStaleRenameDraft(false)
                setDeletedViewConflict(null)
                router.visit(
                  `${routes.reportPath(report.key)}?view_id=default`,
                  {
                    preserveScroll: true,
                  },
                )
              }}
            >
              Return to Default
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  )
}

interface FilterWidgetProps {
  spec: ReportFilterSpec
  value: FilterValue
  today: string
  onChange: (v: FilterValue) => void
}

// Filter widget with label-above-control layout, matching the invoicing
// page's filter bar. The label is always visible (no more "Status: 2
// selected" smashing the label and value together). The control below
// is a compact button that opens a popover with the picker — except
// for booleans, which toggle directly via variant change.
//
// All filters use a consistent w-48 width so the bar reads as a
// uniform row instead of a cloud of differently-sized buttons.
function FilterWidget({ spec, value, today, onChange }: FilterWidgetProps) {
  const [open, setOpen] = useState(false)
  const controlId = `report-filter-${spec.key}`

  if (spec.kind === "boolean") {
    const checked = Boolean(value)
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={controlId} className="text-muted-foreground text-xs">
          {spec.label}
        </Label>
        <Button
          id={controlId}
          variant={checked ? "default" : "outline"}
          onClick={() => onChange(!checked)}
          className="h-11 w-48 justify-start"
        >
          {checked ? "On" : "Off"}
        </Button>
      </div>
    )
  }

  // Trigger shows just the CURRENT VALUE summary — the label lives
  // above. When no value is set, show a muted placeholder.
  const summary = filterSummary(spec, value)
  const triggerText =
    summary ??
    (spec.kind === "combobox"
      ? "All"
      : spec.kind === "multi_select"
        ? "All"
        : spec.kind === "date_range"
          ? "Any date"
          : "All")
  const active = isFilterActive(spec, value)

  // Comboboxes own the Command padding; everything else gets a uniform
  // p-3 popover. Width matches the trigger so the popover doesn't feel
  // disconnected from the control that opened it.
  const contentClass =
    spec.kind === "combobox"
      ? "w-64 p-0"
      : spec.kind === "multi_select"
        ? "w-56 p-3"
        : "w-64 p-3"

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={controlId} className="text-muted-foreground text-xs">
        {spec.label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={controlId}
            variant="outline"
            className={cn(
              "h-11 w-48 justify-between font-normal",
              !active && "text-muted-foreground",
            )}
          >
            <span className="truncate">{triggerText}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className={contentClass}>
          <FilterPicker
            spec={spec}
            value={value}
            today={today}
            onChange={onChange}
            close={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface FilterPickerProps {
  spec: ReportFilterSpec
  value: FilterValue
  today: string
  onChange: (v: FilterValue) => void
  close: () => void
}

function FilterPicker({
  spec,
  value,
  today,
  onChange,
  close,
}: FilterPickerProps) {
  switch (spec.kind) {
    case "date":
      return (
        <div className="space-y-1">
          <Label htmlFor={spec.key} className="text-xs">
            {spec.label}
          </Label>
          <Input
            id={spec.key}
            type="date"
            value={(value as string) || ""}
            max={today}
            onChange={(e) => onChange(e.target.value)}
            className="h-11 w-full"
          />
        </div>
      )

    case "date_range": {
      const v: DateRangeValue = isDateRange(value)
        ? value
        : { start: "", end: "" }
      const setStart = (start: string) =>
        onChange({ start, end: v.end, preset: undefined })
      const setEnd = (end: string) =>
        onChange({ start: v.start, end, preset: undefined })
      return (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor={`${spec.key}-preset`} className="text-xs">
              Relative window
            </Label>
            <Select
              value={v.preset ?? "custom"}
              onValueChange={(preset) => {
                if (preset === "custom") {
                  onChange({ start: v.start, end: v.end })
                  return
                }
                const resolved = resolveReportDatePreset(preset)
                if (resolved) onChange(resolved)
              }}
            >
              <SelectTrigger id={`${spec.key}-preset`} className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_DATE_PRESETS.map((preset) => (
                  <SelectItem key={preset.key} value={preset.key}>
                    {preset.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom dates</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${spec.key}-start`} className="text-xs">
              From
            </Label>
            <Input
              id={`${spec.key}-start`}
              type="date"
              value={v.start}
              max={v.end || today}
              onChange={(e) => setStart(e.target.value)}
              className="h-11 w-full"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${spec.key}-end`} className="text-xs">
              To
            </Label>
            <Input
              id={`${spec.key}-end`}
              type="date"
              value={v.end}
              min={v.start || undefined}
              max={today}
              onChange={(e) => setEnd(e.target.value)}
              className="h-11 w-full"
            />
          </div>
          {!v.preset && (v.start || v.end) && (
            <p className="text-muted-foreground text-xs">
              Saved views and subscriptions remember relative windows only;
              custom calendar dates remain one-time screen filters.
            </p>
          )}
        </div>
      )
    }

    case "string":
      return (
        <div className="space-y-1">
          <Label htmlFor={`${spec.key}-text`} className="sr-only">
            {spec.label}
          </Label>
          <Input
            id={`${spec.key}-text`}
            autoFocus
            type="text"
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={spec.label}
            className="h-11 w-full"
          />
        </div>
      )

    case "select": {
      const opts = (spec.options as ReportFilterOption[] | undefined) ?? []
      return (
        <div className="flex flex-col gap-0.5">
          {opts.map((o) => {
            const selected = value === o.value
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value)
                  close()
                }}
                className={cn(
                  "hover:bg-accent flex min-h-11 items-center rounded-sm px-2 py-1.5 text-left text-sm",
                  selected && "bg-accent",
                )}
              >
                <Check
                  className={cn(
                    "mr-2 size-4",
                    selected ? "opacity-100" : "opacity-0",
                  )}
                />
                {o.label}
              </button>
            )
          })}
        </div>
      )
    }

    case "multi_select": {
      const opts = (spec.options as ReportFilterOption[] | undefined) ?? []
      const selected = (value as string[]) ?? []
      return (
        <div className="space-y-2">
          {opts.map((o) => {
            const id = `${spec.key}-${o.value}`
            const checked = selected.includes(o.value)
            return (
              <label
                key={o.value}
                htmlFor={id}
                className="flex min-h-11 cursor-pointer items-center gap-2 text-sm"
              >
                <Checkbox
                  id={id}
                  checked={checked}
                  onCheckedChange={(c) => {
                    const next = c
                      ? [...selected, o.value]
                      : selected.filter((s) => s !== o.value)
                    onChange(next)
                  }}
                />
                {o.label}
              </label>
            )
          })}
        </div>
      )
    }

    case "combobox": {
      const options = (spec.options as string[] | undefined) ?? []
      const currentVal = (value as string) || ""
      return (
        <Command>
          <CommandInput placeholder={`Search ${spec.label.toLowerCase()}…`} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => {
                    // Re-picking the current value clears the filter,
                    // matching the typical combobox toggle pattern.
                    onChange(currentVal === opt ? "" : opt)
                    close()
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      currentVal === opt ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      )
    }

    default:
      return null
  }
}
